import React, { useState, useEffect, useRef } from 'react';
import { ModalConfirm } from './ModalConfirm';
import { Translate as t } from '../../config/Translate';

// 부모 모달(테마 bootstrap .modal = z-index 1060) 위에 겹쳐 열 때를 위한 값
const STACKED_Z_INDEX = 1075;

/**
 * 예/아니오 확인 대화상자 (window.confirm 대체).
 * - 확인: await onConfirm() 동안 확인·취소 버튼 비활성(이중 제출 방지) → 끝나면 onClose() 로 닫는다.
 * - 취소/배경 클릭/ESC: onClose() (실행 중에는 무시).
 * - onConfirm 이 throw/reject 해도 재전파하지 않고 warn 후 닫는다.
 * - 이벤트가 포털을 타고 부모(카드·행 onClick 등)로 번지지 않게 막는다.
 */
export const ConfirmDialog = ({
	show,
	onClose,
	onConfirm,
	title,
	message,
	confirmLabel,
	cancelLabel,
	confirmCss = 'btn btn-block btn-danger confirm-danger',
	zIndex = STACKED_Z_INDEX,
}) => {
	const [busy, setBusy] = useState(false);
	const mounted = useRef(true);
	const busyRef = useRef(false);

	useEffect(() => {
		mounted.current = true;
		return () => { mounted.current = false; };
	}, []);

	const close = () => { if (!busyRef.current && typeof onClose === 'function') onClose(); };

	const run = async () => {
		if (busyRef.current) return;
		busyRef.current = true;
		setBusy(true);
		try {
			if (typeof onConfirm === 'function') await onConfirm();
		} catch (e) {
			console.warn('[ConfirmDialog] onConfirm failed', { title, reason: e?.message });
		}
		busyRef.current = false;
		if (mounted.current) {
			setBusy(false);
			if (typeof onClose === 'function') onClose();
		}
	};

	return (
		<ModalConfirm
			zIndex={zIndex}
			closeLabel={cancelLabel || t('common.button.cancel')}
			closeDisabled={busy}
			callbackClose={close}
			info={{
				showModal: !!show,
				title: title ?? t('common.message.confirm.areYouSure'),
				content: <div onClick={(e) => e.stopPropagation()}>{message}</div>,
				actions: [{
					key: 'confirm',
					title: busy ? t('common.status.processing') : (confirmLabel || t('common.button.confirm')),
					css: confirmCss,
					disabled: busy,
					callback: run,
				}],
			}} />
	);
};

/**
 * window.confirm 대체 훅.
 *   const { confirm, dialog } = useConfirmDialog();
 *   if (await confirm({ message, danger })) {...}   // JSX 에 {dialog} 를 한 번 둔다
 * 확인 → true, 취소·배경·ESC·언마운트·다른 confirm 호출로 대체 → false.
 */
export const useConfirmDialog = () => {
	// 닫을 때 options 를 지우지 않는다 — 닫히는 동안에도 마지막 제목·본문이 보이게.
	const [state, setState] = useState({ open: false, token: 0, options: {} });
	const resolverRef = useRef(null);
	const tokenRef = useRef(0);

	const settle = (value) => {
		const resolve = resolverRef.current;
		resolverRef.current = null;
		if (resolve) resolve(value);
	};

	useEffect(() => () => settle(false), []);

	const confirm = (options) => new Promise((resolve) => {
		settle(false);
		resolverRef.current = resolve;
		tokenRef.current += 1;
		setState({ open: true, token: tokenRef.current, options: { ...(options || {}) } });
	});

	// ConfirmDialog 는 확인 뒤에도 onClose 를 부른다. 그사이 호출자가 새 confirm 을 열었으면
	// 그 창을 닫거나 false 로 끝내지 않도록, 자기 창(token)일 때만 닫는다.
	const closeIfCurrent = (token) => {
		if (token !== tokenRef.current) return;
		settle(false);
		setState((prev) => (prev.token === token ? { ...prev, open: false } : prev));
	};

	const { open, token, options } = state;
	const dialog = (
		<ConfirmDialog
			show={open}
			title={options.title}
			message={options.message}
			confirmLabel={options.confirmLabel || (options.danger ? t('common.button.delete') : t('common.button.confirm'))}
			confirmCss={options.danger ? 'btn btn-block btn-danger confirm-danger' : 'btn btn-block btn-primary'}
			onConfirm={() => { if (token === tokenRef.current) settle(true); }}
			onClose={() => closeIfCurrent(token)} />
	);

	return { confirm, dialog };
};

export default ConfirmDialog;
